import { useState, useEffect } from 'react';
import { fabricSyncApi } from '@/services/fabric-sync-api';

interface UseFabricEnvironmentResult {
  isDev: boolean;
  lakehouseId: string | null;
  loading: boolean;
}

/**
 * Fetches the Fabric environment info on mount and exposes `isDev`.
 * Returns `{ isDev: false, loading: true }` while loading.
 * Silently defaults to `isDev: false` on error (banner is non-critical).
 */
export function useFabricEnvironment(): UseFabricEnvironmentResult {
  const [isDev, setIsDev] = useState(false);
  const [lakehouseId, setLakehouseId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchEnvironment(): Promise<void> {
      try {
        const env = await fabricSyncApi.getEnvironment();
        if (!cancelled) {
          setIsDev(env.isDev);
          setLakehouseId(env.lakehouseId);
        }
      } catch {
        // Non-critical — default to false / null
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchEnvironment();

    return () => {
      cancelled = true;
    };
  }, []);

  return { isDev, lakehouseId, loading };
}
